<template>
  <div
    class="appointment-report nap-page"
    @keydown="onPageKeydown"
  >
    <div class="nap-page__column">
      <!-- Cabeçalho: voltar, H1 e o stepper do Figma (o report fica com cadeado até o horário da reunião) -->
      <div class="nap-page__header">
        <div class="nap-page__heading">
          <router-link
            :to="{ name: APPOINTMENTS_ROUTE }"
            class="ar-link nap-page__back"
          >
            <feather-icon
              icon="ArrowLeftIcon"
              size="16"
              aria-hidden="true"
            />
            {{ APP_COPY.appointments }}
          </router-link>
          <h1
            :id="ids.title"
            class="nap-page__title"
          >
            {{ FIGMA_COPY.newAppointmentTitle }}
          </h1>
        </div>
        <report-stepper
          class="nap-page__stepper"
          size="lg"
          :current="0"
          lock-upcoming
          :icons="STEPPER_ICONS"
          :captions="stepperCaptions"
        />
      </div>

      <div class="nap-page__grid ar-anim-rise">
        <section
          class="ar-card nap-form"
          :aria-labelledby="ids.title"
          :aria-busy="saving ? 'true' : null"
        >
          <fieldset
            class="nap-form__fieldset"
            :disabled="saving"
          >
            <div class="nap-form__grid">
              <!-- Cliente -->
              <div
                class="nap-field-group"
                :class="{ 'nap-field-group--raised': raisedField === 'customer' }"
                @focusin="raisedField = 'customer'"
              >
                <label
                  :id="ids.customerLabel"
                  :for="ids.customer"
                  class="nap-label"
                >{{ appointmentParticipantLabel }}</label>
                <new-appointment-combobox
                  ref="customer"
                  :value="customer"
                  :options="customerOptions"
                  :input-id="ids.customer"
                  :label-id="ids.customerLabel"
                  :placeholder="NEW_COPY.customerPlaceholder"
                  :empty-text="NEW_COPY.noResults"
                  :loading="loading.customers"
                  :state="fieldState('customer')"
                  :described-by="describedBy('customer')"
                  icon="SearchIcon"
                  remote
                  required
                  @input="onCustomerInput"
                  @search="findCustomers"
                />
                <nap-message :message="fieldMessages.customer" />
              </div>

              <!-- PN Responsável: o PN logado, sem troca nesta tela (como na sidebar) -->
              <div
                ref="consultant"
                role="group"
                class="nap-field-group"
                :aria-labelledby="ids.consultantLabel"
                :aria-describedby="consultantDescribedBy"
              >
                <span
                  :id="ids.consultantLabel"
                  class="nap-label"
                >{{ FIELD_LABELS.consultant }}</span>
                <div class="nap-owner">
                  <span class="nap-owner__chip">
                    <span
                      class="ar-avatar ar-avatar--sm nap-owner__avatar"
                      aria-hidden="true"
                    >{{ consultantInitials }}</span>
                    <span class="nap-owner__name">{{ consultantDisplayName || EMPTY_VALUE }}</span>
                    <feather-icon
                      icon="LockIcon"
                      size="14"
                      class="nap-owner__lock"
                      aria-hidden="true"
                    />
                  </span>
                </div>
                <span
                  :id="ids.consultantHint"
                  class="nap-caption"
                >{{ NEW_COPY.onlyManagersCanChange }}</span>
                <nap-message :message="fieldMessages.consultant" />
              </div>

              <!-- Avisos do cliente escolhido (mesmas regras da sidebar) -->
              <transition name="ar-reveal">
                <div
                  v-if="customerNotices.length"
                  class="nap-span-2"
                >
                  <div class="nap-notices">
                    <p
                      v-for="notice in customerNotices"
                      :key="notice.key"
                      class="ar-callout"
                      :class="`ar-callout--${notice.tone}`"
                    >
                      <feather-icon
                        :icon="notice.icon"
                        size="16"
                        aria-hidden="true"
                      />
                      <span>{{ notice.before }}<strong>{{ notice.strong }}</strong>{{ notice.after }}</span>
                    </p>
                  </div>
                </div>
              </transition>

              <div
                v-if="canSuppressCustomerInvite"
                class="nap-span-2"
              >
                <div class="nap-field-group">
                  <b-form-checkbox
                    :id="ids.suppressInvite"
                    v-model="suppressCustomerInvite"
                    :aria-describedby="suppressCustomerInvite ? ids.suppressInviteHint : null"
                  >
                    {{ suppressCustomerInviteLabel }}
                  </b-form-checkbox>
                  <span
                    v-if="suppressCustomerInvite"
                    :id="ids.suppressInviteHint"
                    class="nap-caption"
                  >{{ suppressCustomerInviteHint }}</span>
                </div>
              </div>

              <transition name="ar-reveal">
                <div
                  v-if="!hasEmail && !customerInviteSuppressed"
                  class="nap-span-2"
                >
                  <div class="nap-field-group">
                    <label
                      :for="ids.customerEmail"
                      class="nap-label"
                    >{{ `E-mail do ${appointmentParticipantName}` }}</label>
                    <div class="nap-control">
                      <feather-icon
                        icon="MailIcon"
                        size="16"
                        class="nap-control__lead"
                        aria-hidden="true"
                      />
                      <input
                        :id="ids.customerEmail"
                        ref="customerEmail"
                        v-model.trim="customerEmail"
                        class="nap-field nap-field--lead"
                        type="email"
                        autocomplete="off"
                        aria-required="true"
                        :aria-invalid="fieldState('customerEmail') ? 'true' : 'false'"
                        :aria-describedby="describedBy('customerEmail')"
                        :data-state="fieldState('customerEmail') || null"
                      >
                    </div>
                    <nap-message :message="fieldMessages.customerEmail" />
                  </div>
                </div>
              </transition>

              <!-- PN Especialista -->
              <div
                class="nap-field-group"
                :class="{ 'nap-field-group--raised': raisedField === 'specialist' }"
                @focusin="raisedField = 'specialist'"
              >
                <label
                  :id="ids.specialistLabel"
                  :for="ids.specialist"
                  class="nap-label"
                >{{ FIELD_LABELS.specialist }}</label>
                <new-appointment-combobox
                  ref="specialist"
                  v-model="consultantSpecialistId"
                  :options="specialistOptions"
                  :input-id="ids.specialist"
                  :label-id="ids.specialistLabel"
                  :placeholder="FIGMA_COPY.specialistPlaceholder"
                  :empty-text="NEW_COPY.noResults"
                  :loading="loading.specialistConsultants"
                  :disabled="!eventType"
                  :state="fieldState('specialist')"
                  :described-by="describedBy('specialist')"
                  icon="UserIcon"
                  chevron
                />
                <nap-message :message="fieldMessages.specialist" />
              </div>

              <!-- Tipo de Compromisso -->
              <div class="nap-field-group">
                <label
                  :for="ids.eventType"
                  class="nap-label"
                >{{ FIELD_LABELS.eventType }}</label>
                <div class="nap-control">
                  <select
                    :id="ids.eventType"
                    ref="eventType"
                    v-model="eventTypeModel"
                    class="nap-field"
                    aria-required="true"
                    :aria-invalid="fieldState('eventType') ? 'true' : 'false'"
                    :aria-describedby="describedBy('eventType')"
                    :aria-busy="loading.eventTypes ? 'true' : null"
                    :data-state="fieldState('eventType') || null"
                  >
                    <option
                      value=""
                      disabled
                    />
                    <option
                      v-for="type in eventTypes"
                      :key="type.id"
                      :value="type.id"
                      :disabled="!disableItem(type)"
                    >
                      {{ type.description }}
                    </option>
                  </select>
                  <feather-icon
                    icon="ChevronDownIcon"
                    size="16"
                    class="nap-control__trail"
                    aria-hidden="true"
                  />
                </div>
                <nap-message :message="fieldMessages.eventType" />
              </div>

              <!-- Data de Início (flatpickr pt-BR) + atalhos de data -->
              <div class="nap-field-group">
                <label
                  :id="ids.startLabel"
                  :for="ids.start"
                  class="nap-label"
                >{{ FIELD_LABELS.start }}</label>
                <div
                  class="nap-control nap-date"
                  :data-state="fieldState('start') || null"
                >
                  <feather-icon
                    icon="CalendarIcon"
                    size="16"
                    class="nap-control__lead"
                    aria-hidden="true"
                  />
                  <flat-pickr
                    ref="startPicker"
                    v-model="appointmentStart"
                    :config="startPickerConfig"
                    :placeholder="FIGMA_COPY.datePlaceholder"
                    @blur="onStartBlur"
                  />
                </div>
                <nap-message :message="fieldMessages.start" />
                <div
                  role="group"
                  class="nap-chips"
                  :aria-labelledby="ids.startLabel"
                >
                  <button
                    v-for="chip in quickDateChips"
                    :key="chip.key"
                    type="button"
                    class="nap-chip"
                    :class="{ 'nap-chip--selected': chip.pressed }"
                    :aria-pressed="chip.pressed ? 'true' : 'false'"
                    @click="pickQuickDate(chip.days)"
                  >
                    <span
                      class="nap-chip__check"
                      aria-hidden="true"
                    >
                      <feather-icon
                        icon="CheckIcon"
                        size="14"
                      />
                    </span>
                    {{ chip.label }}
                  </button>
                </div>
              </div>

              <!-- Data de Término = início + duração (somente leitura) -->
              <div class="nap-field-group">
                <label
                  :for="ids.end"
                  class="nap-label"
                >{{ FIELD_LABELS.end }}</label>
                <output
                  :id="ids.end"
                  class="nap-end ar-tabular"
                  :data-state="scheduleConflict ? 'danger' : null"
                >
                  <feather-icon
                    icon="ClockIcon"
                    size="16"
                    class="nap-end__icon"
                    aria-hidden="true"
                  />
                  <span
                    :key="endText"
                    class="nap-end__value"
                    :class="{ 'nap-end__value--empty': !endText, 'nap-anim-value': endText }"
                  >{{ endText || FIGMA_COPY.datePlaceholder }}</span>
                  <span class="nap-end__duration">{{ NEW_COPY.duration(durationLabel) }}</span>
                </output>
                <div
                  role="radiogroup"
                  class="nap-chips"
                  :aria-label="NEW_COPY.durationLabel"
                >
                  <label
                    v-for="option in APPOINTMENT_DURATIONS"
                    :key="option.minutes"
                    class="nap-chip-radio"
                  >
                    <input
                      v-model="durationMinutes"
                      type="radio"
                      class="ar-sr-only"
                      :name="ids.duration"
                      :value="option.minutes"
                    >
                    <span
                      class="nap-chip"
                      :class="{ 'nap-chip--selected': durationMinutes === option.minutes }"
                    >
                      <span
                        class="nap-chip__check"
                        aria-hidden="true"
                      >
                        <feather-icon
                          icon="CheckIcon"
                          size="14"
                        />
                      </span>
                      {{ option.label }}
                    </span>
                  </label>
                </div>
              </div>

              <!-- Horário ocupado: prévia (âmbar, não impede) ou recusa do servidor ao salvar (vermelho) -->
              <transition name="ar-reveal">
                <div
                  v-if="scheduleNotice"
                  class="nap-span-2 nap-schedule-wrap"
                >
                  <div
                    :id="ids.schedule"
                    class="nap-schedule"
                    :class="`nap-schedule--${scheduleNotice.tone}`"
                  >
                    <feather-icon
                      icon="AlertCircleIcon"
                      size="16"
                      class="nap-schedule__icon"
                      aria-hidden="true"
                    />
                    <span class="nap-schedule__title">{{ scheduleNotice.text }}</span>
                  </div>
                </div>
              </transition>

              <!-- Formato de Reunião: segmented Online | Presencial -->
              <fieldset
                class="nap-span-2 nap-format"
                :aria-describedby="describedBy('meetingFormat')"
              >
                <legend class="nap-legend">
                  {{ APP_COPY.meetingFormatLabel }}
                </legend>
                <div class="nap-format__row">
                  <div
                    class="nap-seg"
                    :class="{ 'nap-seg--invalid': !!fieldState('meetingFormat') }"
                    :style="{ '--nap-seg-count': meetingFormatOptions.length }"
                  >
                    <span
                      v-if="meetingFormatIndex >= 0"
                      class="nap-seg__thumb"
                      :style="{ transform: `translateX(${meetingFormatIndex * 100}%)` }"
                      aria-hidden="true"
                    >
                      <span
                        class="nap-seg__thumb-fill"
                        :class="{ 'nap-seg__thumb-fill--enter': formatThumbEntering }"
                      />
                    </span>
                    <label
                      v-for="option in meetingFormatOptions"
                      :key="option.id"
                      class="nap-seg__option"
                    >
                      <input
                        ref="formatInputs"
                        v-model="meetingFormat"
                        type="radio"
                        class="ar-sr-only"
                        :name="ids.meetingFormat"
                        :value="option.id"
                        aria-required="true"
                      >
                      <span
                        class="nap-seg__text"
                        :class="{ 'nap-seg__text--checked': meetingFormat === option.id }"
                      >
                        <feather-icon
                          :icon="option.icon"
                          size="16"
                          class="nap-seg__icon"
                          aria-hidden="true"
                        />
                        {{ option.label }}
                      </span>
                    </label>
                  </div>
                  <span
                    v-if="!meetingFormat && !fieldMessages.meetingFormat"
                    class="nap-caption"
                  >{{ FIGMA_COPY.selectFormat }}</span>
                  <nap-message :message="fieldMessages.meetingFormat" />
                </div>
              </fieldset>

              <!-- Link da Reunião (online) / Endereço da reunião (presencial) -->
              <transition name="ar-reveal">
                <div
                  v-if="meetingFormat"
                  class="nap-span-2"
                >
                  <div
                    :key="isInPerson ? 'address' : 'link'"
                    class="nap-field-group ar-anim-fade-in"
                  >
                    <label
                      :for="ids.roomLink"
                      class="nap-label"
                    >{{ isInPerson ? APP_COPY.meetingAddressLabel : APP_COPY.meetingLinkLabel }}</label>
                    <div class="nap-control">
                      <feather-icon
                        :icon="isInPerson ? 'MapPinIcon' : 'VideoIcon'"
                        size="16"
                        class="nap-control__lead"
                        aria-hidden="true"
                      />
                      <input
                        v-if="isInPerson"
                        :id="ids.roomLink"
                        ref="roomLink"
                        v-model="meetingAddress"
                        class="nap-field nap-field--lead"
                        type="text"
                        autocomplete="street-address"
                        aria-required="true"
                        :aria-invalid="fieldState('roomLink') ? 'true' : 'false'"
                        :aria-describedby="describedBy('roomLink')"
                        :data-state="fieldState('roomLink') || null"
                        @blur="touch('roomLink')"
                      >
                      <input
                        v-else
                        :id="ids.roomLink"
                        ref="roomLink"
                        v-model="meetingLink"
                        class="nap-field nap-field--lead nap-field--trail"
                        type="url"
                        inputmode="url"
                        autocomplete="off"
                        aria-required="true"
                        :placeholder="FIGMA_COPY.meetingLinkPlaceholder"
                        :aria-invalid="fieldState('roomLink') ? 'true' : 'false'"
                        :aria-describedby="describedBy('roomLink')"
                        :data-state="fieldState('roomLink') || null"
                        @blur="touch('roomLink')"
                      >
                      <feather-icon
                        v-if="!isInPerson && roomLinkValid"
                        icon="CheckIcon"
                        size="16"
                        class="nap-control__trail nap-link-ok ar-anim-chip-pop"
                        aria-hidden="true"
                      />
                    </div>
                    <nap-message :message="fieldMessages.roomLink" />
                  </div>
                </div>
              </transition>

              <!-- Descrição (opcional) -->
              <div class="nap-span-2 nap-field-group">
                <label
                  :for="ids.description"
                  class="nap-label"
                >{{ NEW_COPY.descriptionLabel }}</label>
                <textarea
                  :id="ids.description"
                  v-model="description"
                  class="nap-field"
                  rows="3"
                  :placeholder="NEW_COPY.descriptionPlaceholder"
                />
              </div>
            </div>
          </fieldset>
        </section>

        <new-appointment-summary
          class="nap-page__rail"
          :items="summaryItems"
          :format="summaryFormat"
          :format-label="APP_COPY.meetingFormatLabel"
          :note="summaryNote"
        />
      </div>
    </div>

    <!-- Barra fixa: status (leva ao campo que falta) + Voltar / Salvar -->
    <div class="nap-bar">
      <div class="nap-bar__inner">
        <div class="nap-bar__status">
          <button
            type="button"
            class="nap-bar__status-btn"
            :class="{ 'nap-bar__status-btn--ready': formReady }"
            :aria-describedby="ids.barCaption"
            @click="onStatusClick"
          >
            <transition
              name="ar-fade"
              mode="out-in"
            >
              <span
                :key="statusText"
                class="nap-bar__status-content"
              >
                <feather-icon
                  :icon="formReady ? 'CheckIcon' : 'AlertCircleIcon'"
                  size="16"
                  class="nap-bar__status-icon"
                  aria-hidden="true"
                />
                <span class="ar-tabular">{{ statusText }}</span>
              </span>
            </transition>
          </button>
          <span
            :id="ids.barCaption"
            class="nap-bar__caption ar-tabular"
          >{{ statusCaption }}</span>
        </div>
        <div class="nap-bar__actions">
          <router-link
            :to="{ name: APPOINTMENTS_ROUTE }"
            class="ar-btn ar-btn--secondary nap-bar__back"
          >
            {{ FIGMA_COPY.back }}
          </router-link>
          <button
            ref="saveButton"
            type="button"
            class="ar-btn nap-bar__save"
            :class="saveButtonClasses"
            :aria-disabled="formReady && !saving ? 'false' : 'true'"
            :aria-busy="saving ? 'true' : 'false'"
            :aria-describedby="ids.barCaption"
            aria-keyshortcuts="Control+Enter"
            @click="save"
          >
            <template v-if="saving">
              <svg
                class="ar-spinner"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="9"
                  stroke="currentColor"
                  stroke-opacity=".25"
                  stroke-width="2.5"
                />
                <path
                  d="M21 12a9 9 0 0 0-9-9"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                />
              </svg>
              <span>{{ NEW_COPY.saving }}</span>
            </template>
            <template v-else>
              <span>{{ FIGMA_COPY.save }}</span>
              <kbd
                v-if="formReady"
                class="ar-kbd ar-anim-fade-in"
                aria-hidden="true"
              >{{ NEW_COPY.shortcutHint }}</kbd>
            </template>
          </button>
        </div>
      </div>
    </div>

    <span
      class="ar-sr-only"
      role="status"
      aria-live="polite"
    >{{ announcement }}</span>
  </div>
</template>

<script>
import moment from 'moment'
import { BFormCheckbox } from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
// eslint-disable-next-line import/no-extraneous-dependencies
import { Portuguese } from 'flatpickr/dist/l10n/pt'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import newAppointmentForm, { IN_PERSON_MEETING_FORMAT, ONLINE_MEETING_FORMAT } from '@/modules/appointment/mixins/newAppointmentForm'
import * as appointmentStoreTypes from '@/modules/appointment/store/types'
import { X_MOMENT } from '@/constants/appointment_types'
import {
  APP_COPY,
  APPOINTMENT_DURATIONS,
  APPOINTMENT_FIELD_LABELS,
  APPOINTMENTS_ROUTE,
  FIGMA_COPY,
  NEW_COPY,
  REPORT_ROUTE,
} from '@/constants/appointment_report_v2'
import { isReportPendingError } from '@/helpers/error_messages'
import ReportStepper from '../components/ReportStepper.vue'
import NewAppointmentCombobox from '../components/new-appointment/NewAppointmentCombobox.vue'
import NewAppointmentSummary from '../components/new-appointment/NewAppointmentSummary.vue'
import {
  eventTypeTone,
  initialsOf,
  nextReportUid,
  scrollIntoViewAndFocus,
} from '../helpers/report_ui'

// Formato do valor do flatpickr ('d/m/Y H:i') — o mesmo que a sidebar e o mixin usam ("DD/MM/YYYY HH:mm").
const PICKER_FORMAT = 'DD/MM/YYYY HH:mm'
// Formato do Figma para exibir ("23/02/26 | 13:00").
const DISPLAY_DATE = 'DD/MM/YY'
const DISPLAY_TIME = 'HH:mm'
// Formato de `POST /api/appointments/overlapping_appointments_check` (igual a `stringToDateTime`).
const API_FORMAT = 'YYYY-MM-DD HH:mm'
// O que o PN pode digitar no campo de data (com ou sem a barra, ano com 2 ou 4 dígitos) — sempre com horário.
// Leitura estrita: o parser do flatpickr aceitaria "99/99" como uma data meses à frente.
const TYPED_FORMATS = [
  'DD/MM/YY | HH:mm', 'DD/MM/YY HH:mm', 'DD/MM/YYYY | HH:mm', PICKER_FORMAT,
  'DD/MM/YY | H:mm', 'DD/MM/YY H:mm', 'DD/MM/YYYY | H:mm', 'DD/MM/YYYY H:mm',
]

const parsePickerDate = value => {
  const parsed = moment(String(value || '').trim(), TYPED_FORMATS, true)
  return parsed.isValid() ? parsed.toDate() : undefined
}

const DEFAULT_DURATION = 60
const OVERLAP_CHECK_DELAY_MS = 400
const PULSE_MS = 760
// Enquanto a barra fixa está na tela, esta classe no <body> sobe o botão "voltar ao topo" do layout.
const BAR_BODY_CLASS = 'nap-bar-visible'

const EMPTY_VALUE = '—'
// "(você)" do board, a partir do texto único de `NEW_COPY.selfSuffix`.
const SELF_TAG = NEW_COPY.selfSuffix('').trim()

// Mensagens 405 de `POST /api/appointments` (`AppointmentController::create`) → campo onde aparecem.
const SERVER_RULES = [
  { message: APP_COPY.customerHasMomentX, field: 'customer' },
  { message: APP_COPY.customerOfOtherPn, field: 'customer' },
  { message: APP_COPY.sameSpecialist, field: 'specialist' },
  { message: APP_COPY.timeAlreadyBooked, field: 'schedule' },
  { message: APP_COPY.candidateStageBusy, field: 'eventType' },
  { message: NEW_COPY.startInPast, field: 'schedule' },
]

const QUICK_DATES = [
  { key: 'today', label: NEW_COPY.quickToday, days: 0 },
  { key: 'tomorrow', label: NEW_COPY.quickTomorrow, days: 1 },
  { key: 'week', label: NEW_COPY.quickNextWeek, days: 7 },
]

// Stepper do board v4-novo-compromisso: a etapa atual mostra o ícone de "novo" no lugar do número.
const STEPPER_ICONS = ['FilePlusIcon']

const FORMAT_ICONS = {
  [ONLINE_MEETING_FORMAT]: 'VideoIcon',
  [IN_PERSON_MEETING_FORMAT]: 'MapPinIcon',
}

const FORMAT_LABELS = {
  [ONLINE_MEETING_FORMAT]: NEW_COPY.online,
  [IN_PERSON_MEETING_FORMAT]: NEW_COPY.inPerson,
}

// Ids por instância (rótulos, mensagens e listbox não colidem).
const FIELD_IDS = [
  'title', 'customer', 'customerLabel', 'customerEmail', 'consultantLabel', 'consultantHint',
  'specialist', 'specialistLabel', 'eventType', 'start', 'startLabel', 'end', 'duration', 'schedule',
  'meetingFormat', 'roomLink', 'description', 'barCaption', 'suppressInvite', 'suppressInviteHint',
]

const emptyServerErrors = () => ({
  customer: '',
  specialist: '',
  eventType: '',
  schedule: '',
})

const displayDateTime = value => `${value.format(DISPLAY_DATE)} | ${value.format(DISPLAY_TIME)}`

// Ids da API podem vir como número ou texto (o backend compara com `==`).
const sameId = (a, b) => a !== null && a !== undefined && a !== ''
  && b !== null && b !== undefined && b !== ''
  && String(a) === String(b)

// Mensagem de um campo (`{ id, tone, text }`), com ícone alert-circle 14px (board).
const NapMessage = {
  functional: true,
  props: {
    message: {
      type: Object,
      default: null,
    },
  },
  render(h, { props }) {
    const { message } = props
    if (!message) return null

    return h('p', {
      key: `${message.tone}:${message.text}`,
      attrs: { id: message.id },
      class: ['nap-msg', `nap-msg--${message.tone}`],
    }, [
      h('feather-icon', { props: { icon: 'AlertCircleIcon', size: '14' }, attrs: { 'aria-hidden': 'true' } }),
      h('span', message.text),
    ])
  },
}

/**
 * Novo compromisso — página cheia (rota `appointment-new`, board v4-novo-compromisso: passo 1 da jornada).
 *
 * Regras de negócio, carregamento das opções, validação (Vuelidate) e envio: `mixins/newAppointmentForm`, o mesmo
 * da `NewAppointmentSidebar`. Aqui fica o que é da página: layout do board, datas por atalho + duração, prévia de
 * horário ocupado, mensagens 405 do backend junto do campo, resumo ao lado e a barra fixa.
 * Depois de salvar: Momento X → report do compromisso criado (aguardando horário); outros tipos → Compromissos.
 */
export default {
  name: 'NewAppointmentPage',
  components: {
    BFormCheckbox,
    flatPickr,
    NapMessage,
    NewAppointmentCombobox,
    NewAppointmentSummary,
    ReportStepper,
  },
  mixins: [newAppointmentForm],
  data() {
    const uid = nextReportUid('nap')
    const ids = {}
    FIELD_IDS.forEach(key => {
      ids[key] = `${uid}-${key}`
    })

    return {
      APP_COPY,
      APPOINTMENT_DURATIONS,
      APPOINTMENTS_ROUTE,
      FIELD_LABELS: APPOINTMENT_FIELD_LABELS,
      FIGMA_COPY,
      NEW_COPY,
      EMPTY_VALUE,
      STEPPER_ICONS,
      ids,
      uid,
      durationMinutes: DEFAULT_DURATION,
      meetingLink: '',
      meetingAddress: '',
      eventTypeTouched: false,
      attempted: false,
      touched: {},
      startTypedInvalid: false,
      overlapWarning: false,
      serverErrors: emptyServerErrors(),
      raisedField: '',
      formatThumbEntering: false,
      pulsing: false,
      pulsedOnce: false,
      announcement: '',
      startPickerConfig: {
        dateFormat: 'd/m/Y H:i',
        altInput: true,
        altFormat: 'd/m/y | H:i',
        altInputClass: 'nap-field nap-field--lead ar-tabular',
        allowInput: true,
        parseDate: parsePickerDate,
        enableTime: true,
        time_24hr: true,
        locale: Portuguese,
        minDate: 'today',
      },
    }
  },
  computed: {
    // ---- Datas ----
    startMoment() {
      if (!this.appointmentStart || typeof this.appointmentStart !== 'string') return null
      const value = moment(this.appointmentStart, PICKER_FORMAT, true)
      return value.isValid() ? value : null
    },
    endMoment() {
      return this.startMoment ? this.startMoment.clone().add(this.durationMinutes, 'minutes') : null
    },
    endValue() {
      return this.endMoment ? this.endMoment.format(PICKER_FORMAT) : undefined
    },
    startText() {
      return this.startMoment ? displayDateTime(this.startMoment) : ''
    },
    endText() {
      return this.endMoment ? displayDateTime(this.endMoment) : ''
    },
    durationLabel() {
      const option = APPOINTMENT_DURATIONS.find(item => item.minutes === this.durationMinutes)
      return option ? option.label : `${this.durationMinutes} min`
    },
    quickDateChips() {
      const selected = this.startMoment ? this.startMoment.format('YYYY-MM-DD') : ''
      return QUICK_DATES.map(chip => ({
        ...chip,
        pressed: !!selected && selected === moment().startOf('day').add(chip.days, 'days').format('YYYY-MM-DD'),
      }))
    },
    stepperCaptions() {
      const caption = this.startMoment
        ? NEW_COPY.reportAvailableAt(this.startMoment.format(DISPLAY_TIME), this.startMoment.format(DISPLAY_DATE))
        : NEW_COPY.reportAvailableAtMeeting
      return ['', caption]
    },
    // Chave da prévia de horário ocupado (muda → consulta de novo).
    overlapProbe() {
      if (!this.startMoment || !this.endMoment || !this.consultant) return ''
      return `${this.consultant}|${this.startMoment.format(API_FORMAT)}|${this.endMoment.format(API_FORMAT)}`
    },

    // ---- Opções ----
    customerOptions() {
      return (this.customers || []).map(item => ({
        id: item.id,
        name: item.name,
        initials: initialsOf(item.name),
      }))
    },
    specialistOptions() {
      return this.specialistConsultantsList.map(item => ({
        id: item.id,
        name: item.name,
        initials: initialsOf(item.name),
        suffix: sameId(item.id, this.consultant) ? SELF_TAG : '',
        // Nos tipos de cliente a sidebar mostra "Nível - Nome": aqui o nível vai na 2ª linha.
        caption: item.displayName !== item.name ? item.role_name : '',
      }))
    },
    eventTypeModel: {
      get() {
        return this.eventType === undefined || this.eventType === null ? '' : this.eventType
      },
      set(value) {
        this.eventTypeTouched = true
        this.eventType = value === '' ? undefined : value
      },
    },
    selectedEventType() {
      return (this.appointmentEventTypes || []).find(type => type.id === this.eventType) || null
    },
    meetingFormatOptions() {
      const loaded = this.appointmentMeetingFormatTypes && this.appointmentMeetingFormatTypes.length
        ? this.appointmentMeetingFormatTypes
        : [{ id: ONLINE_MEETING_FORMAT }, { id: IN_PERSON_MEETING_FORMAT }]
      return loaded.map(item => ({
        id: item.id,
        label: FORMAT_LABELS[item.id] || item.description || item.id,
        icon: FORMAT_ICONS[item.id] || 'MonitorIcon',
      }))
    },
    meetingFormatIndex() {
      return this.meetingFormatOptions.findIndex(option => option.id === this.meetingFormat)
    },
    isInPerson() {
      return this.meetingFormat === IN_PERSON_MEETING_FORMAT
    },
    // Link (online) e endereço (presencial) ficam guardados separados: trocar de formato não perde o outro.
    activeRoomLink() {
      if (!this.meetingFormat) return undefined
      const value = this.isInPerson ? this.meetingAddress : this.meetingLink
      return value && value.trim() ? value.trim() : undefined
    },
    // Regras do mixin (required + URL válida no online).
    roomLinkValid() {
      return !!this.meetingFormat && !this.ruleInvalid('roomLink', !this.activeRoomLink)
    },

    // ---- PN Responsável ----
    consultantName() {
      const found = (this.consultants || []).find(item => sameId(item.id, this.consultant))
      if (found && found.name) return found.name
      return this.user && this.user.name ? this.user.name : ''
    },
    consultantDisplayName() {
      if (!this.consultantName) return ''
      const isSelf = !!this.user && sameId(this.consultant, this.user.appify_consultant_id)
      return isSelf ? NEW_COPY.selfSuffix(this.consultantName) : this.consultantName
    },
    consultantInitials() {
      return initialsOf(this.consultantName)
    },
    consultantDescribedBy() {
      return [this.ids.consultantHint, this.describedBy('consultant')].filter(Boolean).join(' ')
    },

    // ---- Avisos do cliente (sidebar: EP rodando, produção atribuída ao dono) ----
    customerNotices() {
      const notices = []
      if (this.hasEpRunning) {
        notices.push({
          key: 'ep',
          tone: 'success',
          icon: 'CheckCircleIcon',
          before: `${APP_COPY.epRunningPrefix} `,
          strong: APP_COPY.epRunningStatus,
          after: '',
        })
      }
      if (this.isProductionOfAnotherOwner) {
        notices.push({
          key: 'owner',
          tone: 'warning',
          icon: 'AlertTriangleIcon',
          before: `${APP_COPY.otherOwnerPrefix} `,
          strong: this.otherOwnersLabel,
          after: APP_COPY.otherOwnerSuffix,
        })
      }
      return notices
    },

    // ---- Regras (as mesmas do backend, checadas na tela quando dá) ----
    // Mesma checagem do backend (`consultant_specialist_id == consultant_id`), já na escolha.
    specialistConflict() {
      if (sameId(this.consultantSpecialistId, this.consultant)) return APP_COPY.sameSpecialist
      return this.serverErrors.specialist
    },
    eventTypeConflict() {
      if (this.isLead && this.eventType === X_MOMENT) return APP_COPY.leadMomentXUnavailable
      return this.serverErrors.eventType
    },
    // Início que já passou (regra do mixin, a mesma do servidor) ou a recusa do servidor para o horário.
    scheduleConflict() {
      if (this.startMoment && this.isStartInPast(this.appointmentStart)) return NEW_COPY.startInPast
      return this.serverErrors.schedule
    },
    // Campos na ordem da tela: `missing` = falta preencher; `conflict` = texto da regra que impede salvar.
    fieldChecks() {
      const check = (key, label, missing, conflict) => ({
        key,
        label,
        missing: !!missing,
        conflict: conflict || '',
      })
      const checks = [check('customer', APPOINTMENT_FIELD_LABELS.customer, !this.customer, this.serverErrors.customer)]
      if (!this.hasEmail && !this.customerInviteSuppressed) {
        checks.push(check('customerEmail', APP_COPY.customerEmailLabel, this.ruleInvalid('customerEmail', !this.customerEmail)))
      }
      checks.push(
        check('consultant', APPOINTMENT_FIELD_LABELS.consultant, !this.consultant),
        check('specialist', APPOINTMENT_FIELD_LABELS.specialist, false, this.specialistConflict),
        check('eventType', APPOINTMENT_FIELD_LABELS.eventType, !this.eventType, this.eventTypeConflict),
        check('start', APPOINTMENT_FIELD_LABELS.start, !this.startMoment, this.scheduleConflict),
        check('meetingFormat', APP_COPY.meetingFormatLabel, !this.meetingFormat),
      )
      if (this.meetingFormat) {
        const label = this.isInPerson ? APP_COPY.meetingAddressLabel : APP_COPY.meetingLinkLabel
        checks.push(check('roomLink', label, !this.roomLinkValid))
      }
      return checks
    },
    missingFields() {
      return this.fieldChecks.filter(check => check.missing)
    },
    conflictFields() {
      return this.fieldChecks.filter(check => !check.missing && !!check.conflict)
    },
    formKind() {
      if (this.missingFields.length) return 'missing'
      return this.conflictFields.length ? 'conflict' : 'ready'
    },
    formReady() {
      return this.formKind === 'ready'
    },

    // ---- Mensagens por campo (`{ id, tone, text }`); tom = borda do campo ----
    fieldMessages() {
      const messages = {}
      const add = (key, tone, text) => {
        messages[key] = { id: `${this.uid}-${key}-msg`, tone, text }
      }
      const showMissing = key => this.attempted && this.missingFields.some(check => check.key === key)

      if (this.serverErrors.customer) add('customer', 'danger', this.serverErrors.customer)
      else if (showMissing('customer')) add('customer', 'warning', NEW_COPY.fieldRequired)

      if (showMissing('customerEmail')) add('customerEmail', 'warning', NEW_COPY.fieldRequired)
      if (showMissing('consultant')) add('consultant', 'warning', NEW_COPY.fieldRequired)
      if (this.specialistConflict) add('specialist', 'danger', this.specialistConflict)

      if (this.eventTypeConflict) add('eventType', 'danger', this.eventTypeConflict)
      else if (showMissing('eventType')) add('eventType', 'warning', NEW_COPY.fieldRequired)

      if (this.startTypedInvalid && !this.startMoment) add('start', 'danger', NEW_COPY.invalidDate)
      else if (showMissing('start')) add('start', 'warning', NEW_COPY.fieldRequired)

      if (showMissing('meetingFormat')) add('meetingFormat', 'warning', FIGMA_COPY.selectFormat)

      if (this.meetingFormat) {
        const hasText = !!this.activeRoomLink
        if (this.isInPerson) {
          if (showMissing('roomLink')) add('roomLink', 'warning', NEW_COPY.fieldRequired)
        } else if ((hasText && !this.roomLinkValid && this.touched.roomLink) || showMissing('roomLink')) {
          add('roomLink', hasText ? 'danger' : 'warning', APP_COPY.invalidMeetingUrl)
        }
      }
      return messages
    },
    // Horário: recusa do servidor (vermelho, impede) ou prévia de conflito (âmbar, só avisa — quem tem
    // "agendamento sobreposto" liberado ainda pode salvar; o servidor decide).
    scheduleNotice() {
      if (this.scheduleConflict) return { tone: 'danger', text: this.scheduleConflict }
      if (this.overlapWarning && this.startMoment) return { tone: 'warning', text: APP_COPY.timeAlreadyBooked }
      return null
    },

    // ---- Barra ----
    statusText() {
      if (this.formKind === 'missing') return NEW_COPY.missingFields(this.missingFields.length)
      if (this.formKind === 'conflict') return NEW_COPY.reviewFields(this.conflictFields.length)
      return NEW_COPY.readyToSave
    },
    statusCaption() {
      if (this.formKind === 'missing') return this.missingFields.map(check => check.label).join(' · ')
      if (this.formKind === 'conflict') return this.conflictFields.map(check => check.label).join(' · ')
      const when = this.startMoment
        ? `${this.startMoment.format(DISPLAY_DATE)} | ${this.startMoment.format(DISPLAY_TIME)}–${this.endMoment.format(DISPLAY_TIME)}`
        : ''
      return [this.customerName, this.selectedEventType && this.selectedEventType.description, when]
        .filter(Boolean)
        .join(' · ')
    },
    saveButtonClasses() {
      return {
        'ar-btn--primary': this.formReady || this.saving,
        'ar-btn--soft': !this.formReady && !this.saving,
        'nap-bar__save--busy': this.saving,
        'ar-anim-pulse': this.pulsing,
      }
    },

    // ---- Resumo ----
    customerName() {
      return this.selectedCustomer ? this.selectedCustomer.name : ''
    },
    specialistPicked() {
      if (!this.consultantSpecialistId) return null
      return this.specialistConsultantsList.find(item => sameId(item.id, this.consultantSpecialistId)) || null
    },
    summaryItems() {
      const specialistName = this.specialistPicked ? this.specialistPicked.name : ''
      return [
        {
          key: 'customer',
          label: APPOINTMENT_FIELD_LABELS.customer,
          text: this.customerName,
          initials: initialsOf(this.customerName),
          warn: !!this.serverErrors.customer,
        },
        {
          key: 'consultant',
          label: APPOINTMENT_FIELD_LABELS.consultant,
          text: this.consultantDisplayName,
          initials: this.consultantInitials,
        },
        {
          key: 'specialist',
          label: APPOINTMENT_FIELD_LABELS.specialist,
          text: specialistName,
          initials: initialsOf(specialistName),
          warn: !!this.specialistConflict,
        },
        {
          key: 'eventType',
          label: APPOINTMENT_FIELD_LABELS.eventType,
          chip: this.selectedEventType ? this.selectedEventType.description : '',
          tone: eventTypeTone(this.eventType),
          warn: !!this.eventTypeConflict,
        },
        {
          key: 'start',
          label: APPOINTMENT_FIELD_LABELS.start,
          text: this.startText,
          warn: !!this.scheduleConflict,
        },
        {
          key: 'end',
          label: APPOINTMENT_FIELD_LABELS.end,
          text: this.endText,
          warn: !!this.scheduleConflict,
        },
      ]
    },
    summaryFormat() {
      const option = this.meetingFormatOptions.find(item => item.id === this.meetingFormat)
      if (!option) return null
      return {
        kind: option.id,
        label: option.label,
        icon: option.icon,
        detail: this.activeRoomLink || '',
      }
    },
    summaryNote() {
      if (!this.startMoment) return NEW_COPY.reportReleaseGeneric
      return NEW_COPY.reportReleaseAt(this.startMoment.format(DISPLAY_TIME), this.startMoment.format(DISPLAY_DATE))
    },
    conflictSignature() {
      return this.fieldChecks.filter(check => check.conflict).map(check => check.key).join('|')
    },
    // Estado do campo de data criado pelo flatpickr (fora do template): muda → reaplica os atributos.
    startInputA11y() {
      return `${this.fieldState('start')}|${this.describedBy('start')}`
    },
  },
  watch: {
    // O término é sempre início + duração (o mixin valida e envia `appointmentEnd`).
    endValue: {
      immediate: true,
      handler(value) {
        this.appointmentEnd = value
      },
    },
    activeRoomLink: {
      immediate: true,
      handler(value) {
        this.roomLink = value
      },
    },
    // Momento X já vem escolhido (board); se o cliente muda a lista (lead PN → tipos de recrutamento), um tipo que
    // não existe mais nela é limpo.
    eventTypes: {
      immediate: true,
      handler(list) {
        if (!list || !list.length) return
        const ids = list.map(type => type.id)
        const hasType = this.eventType !== undefined && this.eventType !== null
        if (hasType && !ids.includes(this.eventType)) {
          this.eventType = undefined
          return
        }
        if (!hasType && !this.eventTypeTouched && ids.includes(X_MOMENT)) this.eventType = X_MOMENT
      },
    },
    // PN Responsável = o PN logado (o perfil pode chegar depois da página).
    'user.appify_consultant_id': {
      immediate: true,
      handler(consultantId) {
        if (!this.consultant && consultantId) this.consultant = consultantId
      },
    },
    customer() {
      this.serverErrors.customer = ''
      // "Este candidato ja possui compromisso ativo para esta etapa." depende do cliente e do tipo.
      this.serverErrors.eventType = ''
      // O e-mail digitado vale só para o cliente sem e-mail em que foi digitado (o backend grava no cadastro).
      this.customerEmail = undefined
    },
    eventType() {
      if (this.serverErrors.customer === APP_COPY.customerHasMomentX) this.serverErrors.customer = ''
      this.serverErrors.eventType = ''
    },
    consultantSpecialistId() {
      this.serverErrors.specialist = ''
    },
    appointmentStart() {
      this.serverErrors.schedule = ''
      if (this.startMoment) this.startTypedInvalid = false
    },
    durationMinutes() {
      this.serverErrors.schedule = ''
    },
    overlapProbe() {
      this.scheduleOverlapCheck()
    },
    meetingFormat(value, oldValue) {
      if (!oldValue && value) {
        this.formatThumbEntering = true
        clearTimeout(this.$_thumbTimer)
        this.$_thumbTimer = setTimeout(() => {
          this.formatThumbEntering = false
        }, 200)
      }
    },
    formReady(isReady, wasReady) {
      if (!isReady || wasReady) return
      this.announce(NEW_COPY.readyToSave)
      if (this.pulsedOnce) return
      this.pulsedOnce = true
      this.pulsing = true
      clearTimeout(this.$_pulseTimer)
      this.$_pulseTimer = setTimeout(() => {
        this.pulsing = false
      }, PULSE_MS)
    },
    // Regra nova apareceu (ex.: escolheu a si mesmo como especialista): anuncia o texto dela.
    conflictSignature(signature, previous) {
      const before = previous ? previous.split('|') : []
      const fresh = this.fieldChecks.find(check => check.conflict && !before.includes(check.key))
      if (fresh && signature) this.announce(fresh.conflict)
    },
    startInputA11y() {
      this.syncStartInputA11y()
    },
  },
  created() {
    this.$_overlapSeq = 0
    this.$_startTyped = ''
    // Lista de clientes limpa: o store é o mesmo das sidebars de agendamento.
    this.mutateCustomers(undefined)
  },
  mounted() {
    document.body.classList.add(BAR_BODY_CLASS)
    this.decorateStartInput()
  },
  beforeDestroy() {
    document.body.classList.remove(BAR_BODY_CLASS)
    clearTimeout(this.$_overlapTimer)
    clearTimeout(this.$_pulseTimer)
    clearTimeout(this.$_thumbTimer)
    this.$_overlapSeq += 1
    const input = this.startInput()
    if (input) input.removeEventListener('input', this.onStartTyping)
    this.mutateCustomers(undefined)
  },
  methods: {
    // ---- Campos ----
    onCustomerInput(id) {
      this.customer = id === null ? undefined : id
    },
    touch(key) {
      if (this.touched[key]) return
      this.touched = { ...this.touched, [key]: true }
    },
    // `$invalid` de uma regra do mixin. O Vuelidate (Vue 2) só cria as regras no beforeMount: antes disso vale o
    // `fallback` (campo vazio = inválido).
    ruleInvalid(key, fallback) {
      const rule = this.v$ && this.v$[key]
      return rule ? rule.$invalid : !!fallback
    },
    fieldState(key) {
      if (key === 'start' && this.scheduleConflict && !this.fieldMessages.start) return 'danger'
      const message = this.fieldMessages[key]
      return message ? message.tone : ''
    },
    describedBy(key) {
      const ids = []
      if (this.fieldMessages[key]) ids.push(this.fieldMessages[key].id)
      if (key === 'start' && this.scheduleNotice) ids.push(this.ids.schedule)
      return ids.join(' ')
    },
    startInput() {
      const picker = this.$refs.startPicker
      if (!picker || !picker.fp) return null
      return picker.fp.altInput || picker.fp.input
    },
    // O flatpickr cria o campo visível (`altInput`, "23/02/26 | 13:00") depois de montar: o rótulo e os
    // atributos de acessibilidade vão para ele.
    decorateStartInput() {
      const input = this.startInput()
      if (!input) return
      input.id = this.ids.start
      input.setAttribute('autocomplete', 'off')
      input.setAttribute('aria-required', 'true')
      input.addEventListener('input', this.onStartTyping)
      this.syncStartInputA11y()
    },
    syncStartInputA11y() {
      const input = this.startInput()
      if (!input) return
      const describedBy = this.describedBy('start')
      input.setAttribute('aria-invalid', this.fieldState('start') ? 'true' : 'false')
      if (describedBy) input.setAttribute('aria-describedby', describedBy)
      else input.removeAttribute('aria-describedby')
    },
    onStartTyping(event) {
      this.$_startTyped = String(event.target.value || '').trim()
    },
    // Texto digitado que o flatpickr não entendeu (ele limpa o campo no blur): "Data ou horário inválido."
    // O `v-model` chega no próximo tick (o flatpickr trata o blur antes deste listener).
    onStartBlur() {
      const typed = this.$_startTyped
      this.$_startTyped = ''
      if (!typed) return
      this.$nextTick(() => {
        this.startTypedInvalid = !this.startMoment
      })
    },
    // Atalho de data: troca o dia e mantém o horário escolhido (sem horário: a próxima hora cheia).
    pickQuickDate(days) {
      const day = moment().startOf('day').add(days, 'days')
      let hour
      let minute
      if (this.startMoment) {
        hour = this.startMoment.hours()
        minute = this.startMoment.minutes()
      } else {
        const next = moment().add(1, 'hour').startOf('hour')
        hour = next.hours()
        minute = 0
        // Depois das 23h, "Hoje" não pode virar amanhã.
        if (days === 0 && !next.isSame(day, 'day')) {
          hour = 23
          minute = 55
        }
      }
      this.appointmentStart = day.clone().hours(hour).minutes(minute).format(PICKER_FORMAT)
      this.startTypedInvalid = false
    },

    // ---- Prévia de horário ocupado ----
    scheduleOverlapCheck() {
      clearTimeout(this.$_overlapTimer)
      this.$_overlapSeq += 1
      this.overlapWarning = false
      if (!this.overlapProbe) return
      const seq = this.$_overlapSeq
      const payload = {
        consultant_id: this.consultant,
        appointment_start: this.startMoment.format(API_FORMAT),
        // O backend grava e compara o término com 1 minuto a menos (encostar em outro compromisso não conflita).
        appointment_end: this.endMoment.clone().subtract(1, 'minute').format(API_FORMAT),
      }
      this.$_overlapTimer = setTimeout(() => {
        this.$store.dispatch(appointmentStoreTypes.CHECK_FOR_OVERLAPPING_APPOINTMENTS, payload)
          .then(() => {
            if (seq === this.$_overlapSeq) this.overlapWarning = false
          })
          .catch(error => {
            if (seq !== this.$_overlapSeq) return
            this.overlapWarning = !!(error && error.response && error.response.status === 405)
          })
      }, OVERLAP_CHECK_DELAY_MS)
    },

    // ---- Foco e anúncios ----
    announce(text) {
      // Mesmo texto duas vezes seguidas: limpa antes para o leitor de tela repetir.
      this.announcement = ''
      this.$nextTick(() => {
        this.announcement = text
      })
    },
    focusTarget(key) {
      switch (key) {
        case 'customer': return this.$refs.customer
        case 'customerEmail': return this.$refs.customerEmail
        case 'consultant': return this.$refs.consultant
        case 'specialist': return this.$refs.specialist
        case 'eventType': return this.$refs.eventType
        case 'start':
        case 'schedule': return this.startInput()
        case 'meetingFormat': {
          const inputs = this.$refs.formatInputs || []
          return inputs[0] || null
        }
        case 'roomLink': return this.$refs.roomLink
        default: return null
      }
    },
    focusField(key) {
      this.$nextTick(() => scrollIntoViewAndFocus(this.focusTarget(key)))
    },
    // "Faltam N campos" / Salvar sem estar pronto: mostra as mensagens e leva ao primeiro campo.
    jumpToFirst() {
      const list = this.formKind === 'missing' ? this.missingFields : this.conflictFields
      if (!list.length) return
      this.attempted = true
      const head = this.formKind === 'missing'
        ? NEW_COPY.missingFields(list.length)
        : NEW_COPY.reviewFields(list.length)
      this.announce(`${head}: ${list.map(check => check.label).join(', ')}.`)
      this.focusField(list[0].key)
    },
    onStatusClick() {
      if (this.formReady) {
        if (this.$refs.saveButton) this.$refs.saveButton.focus()
        return
      }
      this.jumpToFirst()
    },
    onPageKeydown(event) {
      if (event.key !== 'Enter' || !(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) return
      if (event.defaultPrevented || document.body.classList.contains('modal-open')) return
      event.preventDefault()
      this.save()
    },

    // ---- Salvar ----
    async save() {
      if (this.saving) return
      this.refreshNow()
      if (!this.formReady) {
        this.jumpToFirst()
        return
      }
      const isFormCorrect = await this.v$.$validate()
      if (!isFormCorrect) {
        this.attempted = true
        return
      }
      if (!(await this.confirmOtherOwnerProduction())) return

      const { eventType, startMoment: start } = this
      this.saving = true
      let response
      try {
        response = await this.saveAppointment()
      } catch (error) {
        this.saving = false
        this.onSaveError(error)
        return
      }
      // O interceptor resolve com `undefined` quando a sessão caiu (ele mesmo leva ao login).
      if (!response) {
        this.saving = false
        return
      }
      this.onSaved(response.data || {}, eventType, start)
    },
    // Continua "Salvando…" até a navegação (evita segundo envio).
    onSaved(data, eventType, start) {
      const isMomentX = eventType === X_MOMENT
      this.toast({
        component: ToastificationContent,
        props: {
          title: APP_COPY.appointmentCreated,
          text: isMomentX && start
            ? NEW_COPY.reportReleaseToast(start.format(DISPLAY_TIME), start.format(DISPLAY_DATE))
            : undefined,
          icon: 'CheckCircleIcon',
          variant: 'success',
        },
      })
      const location = isMomentX && data.id
        ? { name: REPORT_ROUTE, params: { id: String(data.id) } }
        : { name: APPOINTMENTS_ROUTE }
      this.$router.push(location).catch(() => {})
    },
    // 405 com regra conhecida → mensagem no campo; resto → toast da sidebar (mesmos textos).
    onSaveError(error) {
      if (isReportPendingError(error)) return
      const response = error && error.response
      const message = response && response.status === 405 && response.data && typeof response.data.message === 'string'
        ? response.data.message.trim()
        : ''
      const rule = message ? SERVER_RULES.find(item => item.message === message) : null
      if (!rule) {
        this.notifyAppointmentSaveError(error)
        return
      }
      // O anúncio sai pelo watcher `conflictSignature` (regra nova).
      this.serverErrors = { ...this.serverErrors, [rule.field]: message }
      this.focusField(rule.field)
    },
  },
}
</script>

<style lang="scss" src="@/assets/scss/_appointment-report.scss"></style>
<style lang="scss" src="../components/new-appointment/_new-appointment.scss"></style>

<style lang="scss" scoped>
// Gutter do conteúdo do Oráculo (Vuexy `$content-padding` = 2rem; 1.2rem em telas xs): a barra fixa vai até as
// bordas da coluna de conteúdo (nunca por cima do menu), como no Report.
.nap-page {
  --nap-gutter: 2rem;
  --nap-content-max: 1128px;
  min-height: calc(100vh - 11.1rem);
  display: flex;
  flex-direction: column;
}

.nap-page__column {
  width: 100%;
  max-width: var(--nap-content-max);
  margin: 0 auto;
  padding-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

// ---- Cabeçalho ----
.nap-page__header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px 24px;
}

.nap-page__heading {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.nap-page__back {
  align-self: flex-start;
  font-size: 12px;
  line-height: 16px;
}

.nap-page__title {
  margin: 0;
  font-size: 24px;
  line-height: 32px;
  font-weight: 600;
  letter-spacing: -.2px;
  color: var(--ar-text-1);
}

.nap-page__stepper {
  margin: 0 0 2px;
}

// ---- Formulário (760) + resumo (336), gap 32 ----
.nap-page__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 336px;
  gap: 32px;
  align-items: start;
}

.nap-page__rail {
  position: sticky;
  top: 7rem;
}

.nap-form {
  min-width: 0;
  padding: 24px;
}

.nap-form__fieldset {
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

.nap-form__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 16px;
  row-gap: 20px;
  align-items: start;
}

.nap-span-2 {
  grid-column: 1 / -1;
  min-width: 0;
}

.nap-notices {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

// ---- PN Responsável (chip somente leitura) ----
.nap-owner {
  height: 40px;
  display: flex;
  align-items: center;
}

.nap-owner__chip {
  max-width: 100%;
  height: 32px;
  padding: 0 12px 0 4px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--ar-divider);
  border-radius: var(--ar-radius-pill);
  background: var(--ar-bg-raised);
}

.nap-owner__avatar {
  background: var(--ar-bg-field);
}

.nap-owner__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-1);
}

.nap-owner__lock {
  flex-shrink: 0;
  color: var(--ar-text-muted);
}

// ---- Data de Término (somente leitura) ----
.nap-end {
  height: 40px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid var(--ar-divider);
  border-radius: var(--ar-radius-control);
  background: var(--ar-bg-field);
  transition: border-color var(--ar-dur-hover) var(--ar-ease-standard);

  &[data-state='danger'] {
    border-color: var(--ar-danger);
  }
}

.nap-end__icon {
  flex-shrink: 0;
  color: var(--ar-text-muted);
}

.nap-end__value {
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-1);
  white-space: nowrap;
}

.nap-end__value--empty {
  color: var(--ar-text-placeholder);
}

.nap-end__duration {
  margin-left: auto;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: var(--ar-text-muted);
  white-space: nowrap;
}

// ---- Horário ocupado ----
.nap-schedule-wrap {
  margin-top: -8px;
}

.nap-schedule {
  padding: 10px 12px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border-radius: var(--ar-radius-control);
}

.nap-schedule--danger {
  background: rgba(234, 84, 85, .08);
  border: 1px solid rgba(234, 84, 85, .4);

  .nap-schedule__icon {
    color: var(--ar-danger);
  }

  .nap-schedule__title {
    color: var(--ar-danger-text);
  }
}

.nap-schedule--warning {
  background: var(--ar-warning-tint);
  border: 1px solid var(--ar-warning-border);

  .nap-schedule__icon {
    color: var(--ar-warning);
  }

  .nap-schedule__title {
    color: var(--ar-text-1);
  }
}

.nap-schedule__icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.nap-schedule__title {
  font-size: 13px;
  line-height: 20px;
  font-weight: 600;
}

// ---- Formato de Reunião: segmented 280×40 (thumb que desliza 200ms) ----
.nap-format {
  margin: 0;
  padding: 0;
  border: 0;
}

.nap-format__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
}

.nap-seg {
  --nap-seg-count: 2;
  position: relative;
  width: calc(140px * var(--nap-seg-count));
  max-width: 100%;
  height: 40px;
  padding: 2px;
  display: grid;
  grid-template-columns: repeat(var(--nap-seg-count), minmax(0, 1fr));
  border: 1px solid var(--ar-control);
  border-radius: var(--ar-radius-control);
  background: var(--ar-bg-field);
  transition: border-color var(--ar-dur-hover) var(--ar-ease-standard);
}

.nap-seg--invalid {
  border-color: var(--ar-warning);
}

.nap-seg__thumb {
  position: absolute;
  top: 2px;
  bottom: 2px;
  left: 2px;
  width: calc((100% - 4px) / var(--nap-seg-count));
  pointer-events: none;
  transition: transform var(--ar-dur-thumb) var(--ar-ease-standard);
}

.nap-seg__thumb-fill {
  position: absolute;
  inset: 0;
  border-radius: var(--ar-radius-thumb);
  background: var(--ar-selected-tint);
  box-shadow: inset 0 0 0 1.5px var(--ar-brand-text);
}

.nap-seg__thumb-fill--enter {
  animation: ar-thumb-in var(--ar-dur-fast) var(--ar-ease-standard) both;
}

.nap-seg__option {
  position: relative;
  z-index: 1;
  min-width: 0;
  margin: 0;
  display: flex;
  cursor: pointer;
}

.nap-seg__text {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: var(--ar-radius-thumb);
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-2);
  transition:
    color var(--ar-dur-hover) var(--ar-ease-standard),
    background-color var(--ar-dur-hover) var(--ar-ease-standard);
}

.nap-seg__icon {
  flex-shrink: 0;
  color: var(--ar-text-muted);
  transition: color var(--ar-dur-hover) var(--ar-ease-standard);
}

.nap-seg__text--checked {
  font-weight: 600;
  color: var(--ar-text-1);

  .nap-seg__icon {
    color: var(--ar-brand-text);
  }
}

.nap-seg__option:hover .nap-seg__text:not(.nap-seg__text--checked) {
  background: var(--ar-bg-raised);
}

.nap-seg input:focus-visible + .nap-seg__text {
  outline: 2px solid var(--ar-focus);
  outline-offset: 2px;
}

.nap-link-ok {
  color: var(--ar-success-text);
}

// ---- Barra fixa ----
.nap-bar {
  position: sticky;
  bottom: 0;
  z-index: 20;
  margin: auto calc(-1 * var(--nap-gutter)) 0;
  padding: 0 var(--nap-gutter);
  background: var(--ar-bg-bar);
  backdrop-filter: blur(12px);
  border-top: 1px solid var(--ar-hairline);
  box-shadow: var(--ar-shadow-bar);
}

.nap-bar__inner {
  max-width: var(--nap-content-max);
  min-height: var(--ar-bar-height);
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.nap-bar__status {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.nap-bar__status-btn {
  align-self: flex-start;
  height: 28px;
  margin: 0 0 0 -6px;
  padding: 0 6px;
  border: 0;
  border-radius: var(--ar-radius-control);
  background: transparent;
  font-family: inherit;
  font-size: 13px;
  line-height: 20px;
  font-weight: 600;
  color: var(--ar-text-1);
  white-space: nowrap;
  cursor: pointer;
  transition: background-color var(--ar-dur-hover) var(--ar-ease-standard);

  &:hover {
    background: var(--ar-hairline);
  }
}

.nap-bar__status-content {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.nap-bar__status-icon {
  flex-shrink: 0;
  color: var(--ar-warning);
}

.nap-bar__status-btn--ready .nap-bar__status-icon {
  color: var(--ar-success-text);
}

.nap-bar__caption {
  max-width: 560px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: var(--ar-text-muted);
}

.nap-bar__actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.nap-bar__save {
  min-width: 200px;
  gap: 10px;
  transition:
    background-color 200ms var(--ar-ease-standard),
    border-color 200ms var(--ar-ease-standard),
    color 200ms var(--ar-ease-standard),
    transform var(--ar-dur-press) var(--ar-ease-standard);
}

// Salvando: continua cheio, sem hover/press.
.nap-bar__save--busy,
.nap-bar__save--busy:hover,
.nap-bar__save--busy:active {
  background: var(--ar-brand);
  border-color: var(--ar-brand);
  color: var(--ar-brand-ink);
  transform: none;
  cursor: progress;
}

// ---- Responsivo: resumo embaixo abaixo de xl; uma coluna de campos em telas estreitas ----
@media (max-width: 1199.98px) {
  .nap-page__grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 20px;
  }

  .nap-page__rail {
    position: static;
  }
}

@media (max-width: 575.98px) {
  .nap-page {
    --nap-gutter: 1.2rem;
  }

  .nap-form {
    padding: 16px;
  }

  .nap-form__grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .nap-seg {
    width: 100%;
    height: 48px;
  }

  .nap-bar__inner {
    padding: 12px 0;
    flex-wrap: wrap;
    gap: 12px;
  }

  .nap-bar__caption {
    max-width: 100%;
  }

  .nap-bar__actions {
    width: 100%;
  }

  .nap-bar__back,
  .nap-bar__save {
    flex: 1 1 auto;
    min-width: 0;
    height: 48px;
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";

// Barra fixa do Novo compromisso no rodapé: o botão "voltar ao topo" do layout sobe para não cobrir o "Salvar".
body.nap-bar-visible .btn-scroll-to-top {
  bottom: 88px;
}

// Os botões fixos "Atendimento online" / "Feedback de erro" (App.vue, canto inferior esquerdo) nunca cobrem a barra:
// com o menu aberto (>= 1200px) sobem para cima dela; abaixo disso (menu sobreposto, conteúdo desde a borda) eles
// cobririam o "Voltar" e o "Salvar" — somem enquanto a barra aparece.
body.nap-bar-visible .float-bottom-left {
  bottom: 88px;
}

body.nap-bar-visible .float-bottom-left + .float-bottom-left {
  bottom: 142px;
}

@media (max-width: 1199.98px) {
  body.nap-bar-visible .float-bottom-left {
    display: none;
  }
}

@media (max-width: 575.98px) {
  body.nap-bar-visible .btn-scroll-to-top {
    bottom: 140px;
  }
}
</style>
